import {
	CognitoIdToken,
	CognitoUserSession,
	CognitoAccessToken,
	CognitoRefreshToken,
	CognitoUserPool,
	CognitoUser,
	AuthenticationDetails
} from 'amazon-cognito-identity-js';

import {
	getTechnicianGroupnames,
	getProjectManagerGroupnames,
	getSupervisorGroupnames,
	getSurveyLoaderGroupnames
} from 'aegion_common_utilities/lib/CustomGroupnameMappings';

import {
	getAccessToken,
	getRefreshToken,
	getIdToken
} from '../../scanline/utils/token';
import AWS_GROUP_NAMES from '../permissions/constants';

const isDefined = v => v !== null && v !== undefined;

const isEmptyString = v =>
	isDefined(v) && typeof v === 'string' && v.trim() === '';

const getCognitoIdToken = () => {
	return new CognitoIdToken({ IdToken: getIdToken() });
};
const getCognitoAccessToken = () => {
	return new CognitoAccessToken({ AccessToken: getAccessToken() });
};
const getCognitoRefreshToken = () => {
	return new CognitoRefreshToken({ AccessToken: getRefreshToken() });
};

export const getUserSession = () => {
	const userSession = new CognitoUserSession({
		IdToken: getCognitoIdToken(),
		AccessToken: getCognitoAccessToken(),
		RefreshToken: getCognitoRefreshToken()
	});
	return userSession;
};

export const isTokenValid = () => {
	const userSession = getUserSession();
	return userSession.isValid();
};

export const isTokenExpired = () => {
	const userSession = getUserSession();
	const expireDate = new Date(userSession.accessToken.getExpiration() * 1000);
	return new Date() > expireDate;
};

export const getLoggedInUser = () => {
	const { payload: userData = {} } = getCognitoIdToken();
	const {
		'cognito:username': username = '',
		'cognito:groups': arrCognitoGroups = [],
		email = '',
		given_name: firstName = '',
		family_name: lastName = '',
		sub: uuid = '',
		customer
	} = userData;

	return {
		uuid,
		customer,
		username,
		email,
		firstName,
		lastName,
		fullName: `${firstName} ${lastName}`.trim(),
		cognitoGroups: arrCognitoGroups.reduce((acc, key) => {
			acc[key] = true;
			return acc;
		}, {})
	};
};

export const getUserName = () => {
	const { username } = getLoggedInUser();
	return username;
};

export const getUserUUID = () => {
	const { uuid } = getLoggedInUser();
	return uuid;
};

export const getEmail = () => {
	const { email } = getLoggedInUser();
	return email;
};

export const getCustomer = () => {
	const { customer } = getLoggedInUser();
	return customer;
};

export const getFullName = () => {
	const { fullName } = getLoggedInUser();
	return fullName;
};

export const getUserDisplayName = ({ composite = false } = {}) => {
	const { username, fullName } = getLoggedInUser();

	if (composite && fullName && username) {
		return `${fullName} (${username})`;
	}

	return fullName || username;
};

export const isUserMissingFirstOrLastName = () => {
	const { username, firstName, lastName } = getLoggedInUser();

	const hasUsername = isDefined(username) && !isEmptyString(username);
	const missingFirstName = !isDefined(firstName) || isEmptyString(firstName);
	const missingLastNameName = !isDefined(lastName) || isEmptyString(lastName);

	return hasUsername && (missingFirstName || missingLastNameName);
};

export const hasCognitoGroupName = groupName => {
	const { cognitoGroups = {} } = getLoggedInUser();
	return cognitoGroups[groupName];
};

const getSettingJson = settingString => {
	if (!settingString) {
		return {};
	}

	if (typeof settingString !== 'string') {
		return settingString;
	}

	try {
		return JSON.parse(settingString);
	} catch (e) {
		if (e instanceof SyntaxError) {
			return {};
		}

		throw e;
	}
};

export const getAppSettings = app => {
	const idToken = getCognitoIdToken();

	const appSettingsRaw = idToken.payload[app];
	return getSettingJson(appSettingsRaw);
};

export const isUserInGroup = groupName => {
	const idToken = getCognitoIdToken();
	const { payload: { 'cognito:groups': groups = [] } = {} } = idToken;
	return groups.indexOf(groupName) !== -1;
};

export const getUserSettings = app => {
	const idToken = getCognitoIdToken();
	const {
		payload: { [app]: setting, userSettings: strUserSettings = '{}' } = {}
	} = idToken;
	if (setting) {
		return setting;
	}
	const userSettings = getSettingJson(strUserSettings);
	const { [app]: userAppSettings = {} } = userSettings;
	return userAppSettings;
};

const getUserPool = poolData => {
	return new CognitoUserPool(poolData);
};

const getCognitoUser = (username, poolData) => {
	const userData = {
		Username: username,
		Pool: getUserPool(poolData)
	};
	return new CognitoUser(userData);
};

export const sessionIsTechnician = () => {
	const customer = getCustomer();
	const groupNames = getTechnicianGroupnames(customer);
	return (groupNames || []).some(gn => hasCognitoGroupName(gn));
};

export const sessionIsProjectManager = () => {
	const customer = getCustomer();
	const groupNames = getProjectManagerGroupnames(customer);
	return (groupNames || []).some(gn => hasCognitoGroupName(gn));
};

export const sessionIsSupervisor = () => {
	const customer = getCustomer();
	const groupNames = getSupervisorGroupnames(customer);
	return (groupNames || []).some(gn => hasCognitoGroupName(gn));
};

export const sessionIsAipAdmin = () => {
	return hasCognitoGroupName(AWS_GROUP_NAMES.AIP_ADMINS);
};

export const sessionIsCustomerAdmin = () => {
	return hasCognitoGroupName(AWS_GROUP_NAMES.CUSTOMER_ADMINS);
};

export const sessionIsAegionUser = () => {
	const customer = getCustomer();
	return customer === 'Aegion';
};

export const sessionIsAvManager = () => {
	return hasCognitoGroupName(AWS_GROUP_NAMES.AV_MANAGER);
};

export const sessionIsSurveyLoader = () => {
	const customer = getCustomer();
	if (customer === 'Aegion') {
		return true;
	}

	const groupNames = getSurveyLoaderGroupnames(customer);
	if (groupNames.length) {
		return (groupNames || []).some(gn => hasCognitoGroupName(gn));
	}

	return true;
};

export const sessionStrictIsSurveyLoader = () => {
	const customer = getCustomer();
	const groupNames = getSurveyLoaderGroupnames(customer);
	if (groupNames.length) {
		return (groupNames || []).some(gn => hasCognitoGroupName(gn));
	}

	return true;
};

export const sessionCustomerHasSurveyLoaderPermission = () => {
	const customer = getCustomer();
	return ['Aegion', 'Targa'].indexOf(customer) > -1;
};

export const login = (authenticationData, poolData) => {
	return new Promise((resolve, reject) => {
		const { Username: username } = authenticationData;
		const cognitoUser = getCognitoUser(username, poolData);
		const authenticationDetails = new AuthenticationDetails(authenticationData);
		cognitoUser.authenticateUser(authenticationDetails, {
			onSuccess: result => {
				resolve(result);
			},
			onFailure: error => {
				reject(error);
			}
		});
	});
};
